<template>
  <div
    :class="[
      'sonar-skill-progress',
      isScoreOutdated && 'sonar-skill-progress--outdated',
    ]"
    @mouseenter="showHover(true)"
    @mouseleave="showHover(false)"
  >
    <div class="sonar-skill-progress__header">
      <div class="sonar-skill-progress__title">
        {{ skill.name }}
      </div>
      <div v-if="isJustTested" class="sonar-skill-progress__last-tested">
        Just Tested
      </div>
      <transition name="fade">
        <div v-show="isHovered" class="sonar-skill-progress__proficiency">
          {{ levelLabel }}
        </div>
      </transition>
    </div>
    <div class="sonar-skill-progress__content">
      <SonarProgressBar
        v-for="(progressBar, index) in progressBars"
        :key="index"
        :barProgress="scoreBar(progressBar.score)"
        :index="index"
      />
    </div>
    <InfoIcon
      v-if="isScoreOutdated && isOverviewCard"
      class="sonar-skill-progress__info-icon"
      @click="popupHandler(!isPopupOpen)"
      @mouseleave="popupHandler(isPopupOpen)"
    />
    <transition name="fade">
      <div
        v-if="isPopupOpen"
        ref="reassessPopup"
        v-click-outside="onClickOutside"
        class="sonar-skill-progress__popup-wrapper"
      >
        <XIcon
          class="sonar-skill-progress__popup-close-icon"
          @click="popupHandler(false)"
        />
        <div class="sonar-skill-progress__popup-title">
          This score may be outdated.
        </div>
        <div class="sonar-skill-progress__popup-info">
          Reassess to ensure your score reflects your skill level.
        </div>
        <button
          class="btn btn--primary sonar-skill-progress__popup-cta"
          @click="goToAssessmentHandler()"
        >
          Reassess now
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import SonarProgressBar from '@/sonar/components/SonarProgressBar.vue'
import { mapState } from 'vuex'
import { capitalize } from '@/helpers/textTransform'
import { InfoIcon, XIcon } from 'vue-feather-icons'
import { assessmentHandler } from '@/sonar/helpers/mixins'
import CONSTANTS from '@/constants'

export default {
  name: 'SonarSkillProgress',
  components: {
    SonarProgressBar,
    InfoIcon,
    XIcon,
  },
  mixins: [assessmentHandler],
  props: {
    skill: {
      type: Object,
      required: true,
    },
    isJustTested: {
      type: Boolean,
      required: false,
    },
    isOverviewCard: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      isHovered: false,
      isPopupOpen: false,
    }
  },
  computed: {
    ...mapState({
      assessments: state => state.sonar.assessments,
    }),
    currentScore() {
      return `${this.roundTo2(this.skill.user_status.score)}%`
    },
    progressBars() {
      return this.skill.difficulty_scores
    },
    levelLabel() {
      const { level } = this.skill.user_status
      return capitalize(level)
    },
    isScoreOutdated() {
      return (
        this.skill.user_status.status ===
        CONSTANTS.SONAR.ASSESSMENT.SCORE_PRESERVATION.STATUS.OUTDATED
      )
    },
  },
  methods: {
    roundTo2(n) {
      return Math.floor(n * 100)
    },
    showHover(value) {
      this.isHovered = value
    },
    scoreBar(score) {
      return this.roundTo2(score)
    },
    popupHandler(value) {
      this.isPopupOpen = value
    },
    goToAssessmentHandler() {
      const assessmentId = this.assessments.find(
        a =>
          a.skill_areas[0].id === this.skill.id &&
          a.learning_area_group.name === this.skill.group
      )?.id
      if (assessmentId) this.goToAssessment(assessmentId)
    },
    onClickOutside() {
      this.popupHandler(false)
    },
  },
}
</script>

<style lang="scss" scoped>
.sonar-skill-progress {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 7px;
  height: 24px;
  &--outdated {
    width: calc(100% - 10px);
    @include mobile {
      width: 100%;
    }
  }
  &__header {
    position: absolute;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 24px;
  }
  &__title {
    color: var(--white);
    font-size: rem(12);
    z-index: 10;
    margin-left: 5px;
  }
  &__content {
    display: flex;
    width: 100%;
    gap: 4px;
  }
  &__last-tested {
    display: none;
    position: absolute;
    height: 16px;
    color: var(--white);
    font-size: rem(10);
    line-height: rem(10);
    padding: 3px;
    border-radius: 5px;
    background: var(--purple-gradient);
    left: -65px;
    top: 4.5px;
    @include tablet {
      display: block;
    }
  }
  &__proficiency {
    position: absolute;
    font-size: rem(10);
    line-height: rem(12);
    right: 10px;
    color: var(--white);
    transition: 0.3;
    z-index: 100;
  }
  &__info-icon {
    position: absolute;
    width: 16px;
    height: 24px;
    right: -23px;
    color: var(--apricot-peach);
    cursor: pointer;
  }
  &__popup {
    &-close-icon {
      width: 20px;
      height: 20px;
      color: var(--white);
      opacity: 0.7;
      margin-left: auto;
      cursor: pointer;
      &:hover {
        opacity: 0.5;
      }
    }
    &-wrapper {
      position: absolute;
      right: 0;
      top: -83px;
      padding: 20px;
      z-index: 300;
      display: flex;
      flex-direction: column;
      width: 282px;
      height: 181px;
      border-radius: 10px;
      background: var(--glass-mid-gray), var(--dark-gray);
      z-index: 1000;
      &::after {
        content: ' ';
        position: absolute;
        top: 50%;
        right: -5px;
        border-color: transparent transparent transparent var(--menu-background);
        transform: translateX(50%);
        border-width: 5px;
        border-style: solid;
      }
    }
    &-title {
      font-size: rem(18);
      line-height: rem(24);
      font-weight: 500;
      margin-bottom: 5px;
    }
    &-info {
      font-size: rem(14);
      line-height: rem(20);
      color: var(--light-blue-gray);
    }
    &-cta {
      padding: 8px 16px;
      font-size: rem(14);
      width: fit-content;
      height: fit-content;
      margin: auto 0 0 auto;
    }
  }
}
</style>
